import React, { useState, useEffect } from 'react'
import { api, formatDateTime } from '../../utils/api'
import { StatusBadge } from '../shared'

// Spec 20260825: the DB-backed activation log surfaced under Inventory. Read-only
// — each nightly run and manual activation, what went live and what was held.

const OUTCOME_TYPE = {
  activated: 'success',
  held_unpaid: 'warning',
  held_unapproved: 'warning',
  pricing_failed: 'error',
}
const OUTCOME_LABEL = {
  activated: 'Activated',
  held_unpaid: 'Held — unpaid',
  held_unapproved: 'Held — unapproved',
  pricing_failed: 'Pricing failed',
}

function ActivationHistory() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let alive = true
    api.get('/api/listings/activation-log')
      .then((r) => { if (alive) setRows(r?.items || []) })
      .catch((e) => { if (alive) setError(e.message || 'Failed to load activation history') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  // Group rows into runs by their shared run_at (newest first, already sorted).
  const runs = []
  const byRun = new Map()
  for (const row of rows) {
    if (!byRun.has(row.run_at)) {
      const group = { run_at: row.run_at, items: [] }
      byRun.set(row.run_at, group)
      runs.push(group)
    }
    byRun.get(row.run_at).items.push(row)
  }

  if (loading) {
    return <div style={{ padding: 'var(--space-6)', textAlign: 'center' }} className="text-muted">Loading…</div>
  }
  if (error) {
    return (
      <div className="card" style={{ padding: 'var(--space-4)' }}>
        <span className="text-error">{error}</span>
      </div>
    )
  }

  return (
    <div>
      <h2 style={{ marginBottom: 'var(--space-2)' }}>Activation History</h2>
      <p className="text-muted text-sm" style={{ marginBottom: 'var(--space-4)' }}>
        Each nightly run and manual activation — what went live, and what was held back and why.
      </p>

      {runs.length === 0 ? (
        <div className="card empty-state">
          <div className="empty-state-title">No activation runs yet</div>
          <div className="empty-state-description">
            Activated and held drafts will appear here after the nightly job runs.
          </div>
        </div>
      ) : (
        runs.map((run, i) => {
          const activated = run.items.filter((r) => r.outcome === 'activated').length
          const trigger = run.items[0]?.trigger
          return (
            <div className="card" key={`${run.run_at}-${i}`} style={{ marginBottom: 'var(--space-4)' }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: 'var(--space-3)',
                  borderBottom: '1px solid var(--color-border)',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)',
                }}
              >
                <strong>{formatDateTime(run.run_at)}</strong>
                <span className="text-sm text-muted">
                  {activated} activated · {run.items.length - activated} held/failed
                  {trigger ? ` · ${trigger}` : ''}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th style={{ width: 160 }}>Outcome</th>
                      <th>Reason</th>
                      <th style={{ width: 120 }}>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {run.items.map((r, j) => (
                      <tr key={`${r.shopify_product_id}-${j}`}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.shopify_product_id}</td>
                        <td>
                          <StatusBadge
                            status={OUTCOME_LABEL[r.outcome] || r.outcome}
                            type={OUTCOME_TYPE[r.outcome] || 'default'}
                          />
                        </td>
                        <td className="text-sm text-muted">{r.reason || '—'}</td>
                        <td className="text-sm">
                          {r.trigger === 'manual' ? (r.actor_name || r.actor_user_id || 'admin') : 'nightly'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )
        })
      )}
    </div>
  )
}

export default ActivationHistory
